/**
 * Calcula el saldo y los meses pendientes de un cliente de DtProvidencia
 *
 * Regla usada:
 * - El cobro es por mes anticipado.
 * - Si la fecha de contratación fue, por ejemplo, el 05-11-2025:
 *   - 05-11 a 05-12 = mes 1
 *   - 05-12 a 05-01 = mes 2
 *   - etc.
 * - Si hoy ya pasó la fecha de inicio de un nuevo período, ese período ya corresponde cobrarlo.
 *
 * Ejemplo:
 * - Fecha contratación: 2025-11-05
 * - Hoy: 2026-03-09
 * - Períodos cobrables:
 *   05-11 a 05-12
 *   05-12 a 05-01
 *   05-01 a 05-02
 *   05-02 a 05-03
 *   05-03 a 05-04
 * - Resultado: 5 meses pendientes
 *
 * @param {Object} objeto - Objeto con los datos del cliente
 * @returns {Object} - Objeto con { saldo, mesesPendientes }
 */
export const calcularSaldoDtProvidencia = (objeto) => {
	const VALOR_MENSUAL = 11990;
	const UMBRAL_PAGADO = 2090;

	// Si el cliente no está vigente y debe ser sacado, retorna 0
	if (objeto.vigente === "No" && objeto.sacar === "Si") {
		return {
			saldo: 0,
			mesesPendientes: 0
		};
	}

	// Parse seguro de fecha YYYY-MM-DD evitando problemas de zona horaria
	const parseFechaLocal = (fechaStr) => {
		if (!fechaStr || typeof fechaStr !== "string") return null;

		const partes = fechaStr.split("-");
		if (partes.length !== 3) return null;

		const [year, month, day] = partes.map(Number);
		if (!year || !month || !day) return null;

		return new Date(year, month - 1, day);
	};

	// Normaliza una fecha a medianoche local
	const normalizarFecha = (fecha) => {
		const copia = new Date(fecha);
		copia.setHours(0, 0, 0, 0);
		return copia;
	};

	// Suma meses respetando el día original, ajustando al último día válido del mes
	// Ej:
	// 2026-01-31 + 1 mes = 2026-02-28
	// 2024-01-31 + 1 mes = 2024-02-29
	const sumarMesesAjustado = (fechaBase, cantidadMeses) => {
		const year = fechaBase.getFullYear();
		const month = fechaBase.getMonth();
		const day = fechaBase.getDate();

		const totalMonths = month + cantidadMeses;
		const targetYear = year + Math.floor(totalMonths / 12);
		const targetMonth = ((totalMonths % 12) + 12) % 12;

		const ultimoDiaMesDestino = new Date(targetYear, targetMonth + 1, 0).getDate();
		const diaFinal = Math.min(day, ultimoDiaMesDestino);

		return new Date(targetYear, targetMonth, diaFinal);
	};

	const fechaContratacion = parseFechaLocal(objeto.fechaContratacion);
	if (!fechaContratacion || Number.isNaN(fechaContratacion.getTime())) {
		return {
			saldo: 0,
			mesesPendientes: 0
		};
	}

	const fechaHoy = normalizarFecha(new Date());
	const fechaInicio = normalizarFecha(fechaContratacion);

	// Si la contratación es futura, no hay nada que cobrar aún
	if (fechaInicio > fechaHoy) {
		return {
			saldo: 0,
			mesesPendientes: 0
		};
	}

	/**
	 * Lógica:
	 * - El primer período se cobra desde la fecha de contratación.
	 * - Por lo tanto, si hoy es igual o posterior a la fecha de contratación, ya hay al menos 1 período cobrable.
	 * - Luego vamos sumando meses mientras la fecha de inicio de cada nuevo período
	 *   sea menor o igual a hoy.
	 *
	 * Ejemplo contratación 2025-11-05 y hoy 2026-03-09:
	 * - período 1 inicia 2025-11-05
	 * - período 2 inicia 2025-12-05
	 * - período 3 inicia 2026-01-05
	 * - período 4 inicia 2026-02-05
	 * - período 5 inicia 2026-03-05
	 * => total 5
	 */
	let mesesPendientes = 0;
	let indicePeriodo = 0;

	while (true) {
		const inicioPeriodo = normalizarFecha(sumarMesesAjustado(fechaInicio, indicePeriodo));

		if (inicioPeriodo <= fechaHoy) {
			mesesPendientes++;
			indicePeriodo++;
		} else {
			break;
		}
	}

	const mesesPagados2023 = objeto.mesesPagados2023
		? parseInt(objeto.mesesPagados2023, 10)
		: 0;

	const mesesPagados = Number.isNaN(mesesPagados2023) ? 0 : mesesPagados2023;

	const saldoCliente = (mesesPendientes * VALOR_MENSUAL) - (mesesPagados * VALOR_MENSUAL);

	let saldoFinal;
	if (saldoCliente <= 0 || saldoCliente < UMBRAL_PAGADO) {
		saldoFinal = "Pagado";
	} else {
		saldoFinal = saldoCliente;
	}

	return {
		saldo: saldoFinal,
		mesesPendientes
	};
};